import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, isRedirect, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { toast } from 'sonner';
import { MCP_SCOPES, SCOPE_GROUPS } from '@logspot-platform/platform-types';
import { authClient } from '@/lib/auth-client';
import { LinkLogo } from '@/components/nav/link-logo';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { organizationsOptions, sessionOptions } from '@/hooks/query-options';
import { userHasOrganization } from '@/lib/org-gate';
import { getOAuthResumeURL } from '@/lib/auth-redirect';

// OAuth 2.1 consent screen for the Logspot MCP server.
//
// Better Auth's `oauthProvider` redirects the user here (configured as
// `consentPage` in packages/auth/src/auth.ts) with the SIGNED OAuth request as
// query parameters (`client_id`, `scope`, `redirect_uri`, `state`, `exp`,
// `sig`, …). We display the requesting client + the requested scopes, then POST
// the decision to `/oauth2/consent` via `authClient.oauth2.consent`. The
// endpoint is stateless across the redirect, so it requires `oauth_query` — the
// full signed query string this page was loaded with — to re-establish and
// verify the OAuth request context; we pass `window.location.search` for that.
// On success the endpoint returns `{ redirect: true, url }` and we send the
// browser to `url` (which carries the authorization code back to the client).

const consentSearchSchema = z.object({
  client_id: z.string().optional(),
  scope: z.string().optional(),
  redirect_uri: z.string().optional(),
});

// API scope labels come from the shared taxonomy; the OIDC scopes are labeled here.
const SCOPE_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    SCOPE_GROUPS.flatMap((group) =>
      group.scopes.map((scope) => [scope.scope, scope.description]),
    ),
  ),
  openid: 'Access to your basic profile identity',
  profile: 'Access to your profile information',
  email: 'Access to your email address',
  offline_access: 'The application to refresh expired access automatically',
};

const describeScope = (scope: string): string =>
  SCOPE_LABELS[scope] ?? `Access to "${scope}"`;

// API scopes are optional per grant; the OIDC scopes stay fixed so sign-in never breaks.
const API_SCOPE_KEYS = new Set<string>(
  SCOPE_GROUPS.flatMap((group) => group.scopes.map((scope) => scope.scope)),
);
const isApiScope = (scope: string): boolean => API_SCOPE_KEYS.has(scope);
const MCP_SCOPE_KEYS = new Set<string>(MCP_SCOPES);
const isMcpScope = (scope: string): boolean => MCP_SCOPE_KEYS.has(scope);
// Writes start unchecked: acting on the user's behalf is an explicit per-connection opt-in.
const isWriteScope = (scope: string): boolean =>
  isApiScope(scope) && scope.endsWith(':write');

export const Route = createFileRoute('/consent')({
  component: Consent,
  validateSearch: consentSearchSchema,
  // Consent requires an authenticated session (the /oauth2/consent endpoint is
  // session-gated). Better Auth's OAuth provider reaches this page via a HARD
  // navigation (a server-side redirect), so the in-memory session atom backing
  // `context.isAuthenticated()` is NOT hydrated yet on first paint — using it
  // here would bounce a logged-in user to sign-in. Mirror `_auth.tsx`: AWAIT
  // the session query (warming the cache the loader also reads), then gate on
  // the resolved value. Send unauthenticated users to sign-in, returning them
  // to this exact consent URL (query intact) afterwards.
  beforeLoad: async ({ context, location }) => {
    let session;
    try {
      session = await context.queryClient.ensureQueryData(sessionOptions());
    } catch (error) {
      if (isRedirect(error)) throw error;
      session = null;
    }
    if (!session?.user) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: getOAuthResumeURL(location.searchStr) ?? location.href },
      });
    }
    // A signed-in user with no organization would get a token that every MCP
    // tool rejects. Send them through onboarding first; it returns them here.
    if (session?.user) {
      const userId = session.user.id;
      const hasOrg = await userHasOrganization(
        session.session?.activeOrganizationId,
        () =>
          context.queryClient.ensureQueryData(organizationsOptions({ userId })),
      );
      if (!hasOrg) {
        // A resume link, not this signed URL: its signature expires in 10 minutes.
        throw redirect({
          to: '/onboarding',
          search: { redirect: getOAuthResumeURL(location.searchStr) ?? location.href },
        });
      }
    }
  },
  // Load the session for the user's email (shown in the consent copy). No
  // useEffect — the router loader hydrates the TanStack Query cache up-front.
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionOptions());
    return { userEmail: session?.user?.email ?? null };
  },
});

function Consent() {
  const { client_id: clientId, scope } = Route.useSearch();
  const { userEmail } = Route.useLoaderData();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Public client fields (name, homepage) so the user sees who is asking, not a random id.
  const { data: client } = useQuery({
    queryKey: ['oauth-public-client', clientId] as const,
    queryFn: async () => {
      const { data, error } = await authClient.oauth2.publicClient({
        query: { client_id: clientId ?? '' },
      });
      if (error) throw new Error(error.message ?? 'Could not load client');
      return data;
    },
    enabled: Boolean(clientId),
    staleTime: 5 * 60 * 1000,
  });

  // Only scopes the client actually requested can be granted; the endpoint rejects any other.
  const requestedScopes = [
    ...new Set((scope ?? '').split(' ').filter(Boolean)),
  ];
  // API scopes no MCP tool uses (ingest, privacy requests) are never granted to an
  // assistant, whatever the client asked for.
  const unavailableScopes = requestedScopes.filter(
    (requestedScope) =>
      isApiScope(requestedScope) && !isMcpScope(requestedScope),
  );
  const [declined, setDeclined] = useState<string[]>(() => [
    ...requestedScopes.filter(isWriteScope),
    ...unavailableScopes,
  ]);
  const grantedScopes = requestedScopes.filter(
    (requestedScope) => !declined.includes(requestedScope),
  );
  const requestedApiScopes = requestedScopes.filter(
    (requestedScope) =>
      isApiScope(requestedScope) && !unavailableScopes.includes(requestedScope),
  );
  const requestedReadScopes = requestedApiScopes.filter(
    (requestedScope) => !isWriteScope(requestedScope),
  );
  const requestedWriteScopes = requestedApiScopes.filter(isWriteScope);
  const noApiScopeLeft =
    requestedApiScopes.length > 0 && !grantedScopes.some(isApiScope);
  const clientLabel = client?.client_name ?? clientId ?? 'An application';
  // Two choices by default (read, make changes); Customize opens the per-permission list.
  const [customize, setCustomize] = useState(false);

  const toggleScopes = (scopes: string[], checked: boolean) =>
    setDeclined((current) =>
      checked
        ? current.filter((item) => !scopes.includes(item))
        : [...new Set([...current, ...scopes])],
    );
  // Tri-state so a partial grant made under Customize never reads as "off" in the simple view.
  const groupState = (scopes: string[]): boolean | 'indeterminate' => {
    const granted = scopes.filter((item) => !declined.includes(item)).length;
    if (granted === 0) return false;
    return granted === scopes.length ? true : 'indeterminate';
  };

  const submitDecision = async (accept: boolean) => {
    setIsSubmitting(true);
    // The signed OAuth query this page was loaded with — required by the
    // stateless /oauth2/consent endpoint to re-establish the request context.
    // `window.location.search` includes the leading "?"; the endpoint accepts
    // either form, but trim it for cleanliness.
    const oauthQuery = window.location.search.replace(/^\?/, '');

    // Only the checked scopes are granted; the endpoint rejects anything not requested.
    const { data, error } = await authClient.oauth2.consent({
      accept,
      oauth_query: oauthQuery,
      ...(accept && declined.length > 0 ? { scope: grantedScopes.join(' ') } : {}),
    });

    if (error || !data?.redirect || !data.url) {
      setIsSubmitting(false);
      toast.error('Could not complete authorization', {
        description:
          error?.message ??
          'Something went wrong while processing your decision. Please try ' +
            'starting the connection again.',
      });
      return;
    }

    // Hand control back to the OAuth client: `data.url` carries the
    // authorization code (accept) or an access_denied error (deny) on the
    // client's redirect_uri. This leaves the SPA, so a full navigation is
    // correct here (not the TanStack router).
    window.location.href = data.url;
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-md">
          <div className="flex flex-col items-center">
            <LinkLogo />
          </div>

          {/* `flex flex-col gap-6` restores the inter-section rhythm the shared
              Card omits (it is `py-6` only, no gap) — without it CardHeader,
              CardContent and CardFooter butt together. */}
          <Card className="mt-10 flex flex-col gap-6">
            <CardHeader>
              <CardTitle>Authorize Access</CardTitle>
              <CardDescription>
                <span className="font-medium">{clientLabel}</span> wants to
                connect to your Logspot account
                {userEmail ? (
                  <>
                    {' '}
                    (<span className="font-medium">{userEmail}</span>)
                  </>
                ) : null}
                .
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <p className="text-sm font-medium">This will allow:</p>
              {customize ? (
                <ul className="space-y-2">
                  {requestedScopes.map((requestedScope) =>
                    unavailableScopes.includes(requestedScope) ? null : isApiScope(
                        requestedScope,
                      ) ? (
                      <li key={requestedScope}>
                        <label className="flex items-start gap-2 text-sm">
                          <Checkbox
                            checked={!declined.includes(requestedScope)}
                            onCheckedChange={(checked) =>
                              toggleScopes([requestedScope], checked === true)
                            }
                          />
                          <span>{describeScope(requestedScope)}</span>
                        </label>
                      </li>
                    ) : (
                      <li
                        key={requestedScope}
                        className="align-start flex items-center gap-2 text-sm"
                      >
                        <span aria-hidden className="text-primary">
                          •
                        </span>
                        <span>{describeScope(requestedScope)}</span>
                      </li>
                    ),
                  )}
                </ul>
              ) : (
                <ul className="space-y-3">
                  {requestedReadScopes.length > 0 ? (
                    <li>
                      <label className="flex items-start gap-2 text-sm">
                        <Checkbox
                          checked={groupState(requestedReadScopes)}
                          onCheckedChange={(checked) =>
                            toggleScopes(requestedReadScopes, checked === true)
                          }
                        />
                        <span>
                          <span className="font-medium">Read your data</span>
                          <span className="text-muted-foreground block">
                            Analytics, events, and account information, without
                            changing anything.
                          </span>
                        </span>
                      </label>
                    </li>
                  ) : null}
                  {requestedWriteScopes.length > 0 ? (
                    <li>
                      <label className="flex items-start gap-2 text-sm">
                        <Checkbox
                          checked={groupState(requestedWriteScopes)}
                          onCheckedChange={(checked) =>
                            toggleScopes(requestedWriteScopes, checked === true)
                          }
                        />
                        <span>
                          <span className="font-medium">
                            Make changes on your behalf
                          </span>
                          <span className="text-muted-foreground block">
                            Create or change data in your account. Off unless
                            you turn it on.
                          </span>
                        </span>
                      </label>
                    </li>
                  ) : null}
                  {requestedApiScopes.length === 0 ? (
                    <li className="text-muted-foreground text-sm">
                      Sign in with your Logspot identity only.
                    </li>
                  ) : null}
                </ul>
              )}
              {requestedApiScopes.length > 0 ? (
                <div className="space-y-1">
                  {customize ? (
                    <p className="text-muted-foreground text-sm">
                      Tools that need an unchecked permission are not offered.
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto px-0"
                    onClick={() => setCustomize((current) => !current)}
                  >
                    {customize ? 'Simple View' : 'Customize Permissions'}
                  </Button>
                </div>
              ) : null}
              <p className="text-muted-foreground text-sm">
                You can revoke access anytime under Connected Applications in
                your Logspot settings.
              </p>
            </CardContent>

            <CardFooter className="flex gap-3 pb-0">
              <Button
                className="flex-1"
                variant="outline"
                type="button"
                disabled={isSubmitting}
                onClick={() => submitDecision(false)}
              >
                Deny
              </Button>
              <Button
                className="flex-1"
                type="button"
                disabled={isSubmitting || noApiScopeLeft}
                onClick={() => submitDecision(true)}
              >
                {isSubmitting ? 'Authorizing…' : 'Allow'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </section>
  );
}
